import { ChangeEvent, useEffect, useState } from "react";
import { createRoute, updateRoute } from "../../services";
import { RouteManagementRow, RouteSlot } from "../../types/route.type";
import ConfirmModal from "../ConfirmModal";
import {
  ROUTE_NAME_MAX_LENGTH,
  formatRouteSeq,
  readRouteNameError,
} from "./routeText";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

const DIALOG_CONTENT_WIDTH = 400;

/** Creating picks a free number of the branch; renaming keeps the route's number. */
export type RouteNameDialogMode =
  | { kind: "create"; slots: RouteSlot[]; branchUuid?: string }
  | { kind: "rename"; route: RouteManagementRow };

interface RouteNameDialogProps {
  /** null keeps the dialog closed. */
  mode: RouteNameDialogMode | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function RouteNameDialog({
  mode,
  onClose,
  onSaved,
}: RouteNameDialogProps) {
  const [routeSeq, setRouteSeq] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const isCreate = mode?.kind === "create";
  const initialName = mode?.kind === "rename" ? mode.route.route_name : "";
  const firstFreeSeq = mode?.kind === "create" ? mode.slots[0]?.route_seq : undefined;

  // Every opening starts from the route's stored name (or the first free number), so a
  // cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!mode) return;
    setName(initialName);
    setRouteSeq(firstFreeSeq === undefined ? "" : String(firstFreeSeq));
  }, [mode, initialName, firstFreeSeq]);

  const nameError = readRouteNameError(name);
  // An untouched field keeps quiet: the create form opens on an empty name.
  const visibleNameError = name === initialName ? undefined : nameError ?? undefined;
  const isUnchanged = !isCreate && name.trim() === initialName;
  const isIncomplete = nameError !== null || (isCreate && routeSeq === "");

  /** Answers whether the API stored the route. */
  const saveRoute = async (): Promise<boolean> => {
    if (!mode) return false;
    const routeName = name.trim();
    try {
      if (mode.kind === "create") {
        await createRoute({
          branch_uuid: mode.branchUuid,
          route_seq: Number(routeSeq),
          route_name: routeName,
        });
      } else {
        await updateRoute(
          mode.route.uuid,
          { route_name: routeName },
          "แก้ชื่อสายส่งสำเร็จ",
        );
      }
      return true;
    } catch {
      // The interceptor already showed the API's reason; the dialog stays open on the
      // typed values so they can be corrected or saved again.
      return false;
    }
  };

  const handleSave = async () => {
    if (isIncomplete || isUnchanged) return;
    setSaving(true);
    const saved = await saveRoute();
    setSaving(false);
    if (!saved) return;
    onSaved();
    onClose();
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleChangeSeq = (event: ChangeEvent<HTMLInputElement>) => {
    setRouteSeq(event.target.value);
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const title =
    mode?.kind === "rename"
      ? `แก้ชื่อสาย ${mode.route.route_code}`
      : "สร้างสายส่ง";

  return (
    <ConfirmModal
      open={Boolean(mode)}
      title={title}
      confirmLabel={isCreate ? "สร้างสาย" : "บันทึก"}
      confirmDisabled={isIncomplete || isUnchanged}
      loading={saving}
      onClose={handleClose}
      onConfirm={handleSave}
    >
      <Stack
        gap={2}
        sx={{ width: DIALOG_CONTENT_WIDTH, maxWidth: "100%", pt: 1 }}
      >
        {mode?.kind === "create" && (
          <TextField
            select
            size="small"
            label="เลขสาย"
            value={routeSeq}
            onChange={handleChangeSeq}
            helperText="เลือกได้เฉพาะเลขที่ว่าง (01–16) ระบบตั้งรหัสสายจากเลขนี้ และแก้เลขภายหลังไม่ได้"
          >
            {mode.slots.map((slot) => (
              <MenuItem key={slot.route_seq} value={String(slot.route_seq)}>
                {formatRouteSeq(slot.route_seq)} — {slot.route_code}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          size="small"
          label="ชื่อสาย"
          value={name}
          onChange={handleChangeName}
          error={Boolean(visibleNameError)}
          helperText={visibleNameError}
          slotProps={{ htmlInput: { maxLength: ROUTE_NAME_MAX_LENGTH } }}
        />
        {isCreate && (
          <Alert severity="info">
            สายใหม่เริ่มในโหมดทดสอบ (เลขบิลขึ้นต้นด้วย T และไม่ส่ง e-Tax)
            จนกว่าผู้ดูแลระบบจะเปิดใช้งานจริง ถ้าเลขนี้เคยถูกลบ
            ระบบจะนำสายเดิมกลับมาใช้
          </Alert>
        )}
      </Stack>
    </ConfirmModal>
  );
}
